import { Breadcrumbs } from "@/components/breadcrumbs/Breadcrumbs.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { HelpArticleLink } from "@/pages/help/components/HelpArticleLink.tsx";
import { HelpCollectionCards } from "@/pages/help/components/HelpCollectionCards.tsx";

import { useHelp } from "@/pages/help/help.hook.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { resolveHelpLanguage } from "@/utils/help-language.ts";
import { Box, ButtonIcon, FontAwesomeIcon, Page, Skeleton, Stack, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const HelpCollection = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{
    slug: string;
  }>();
  const language = resolveHelpLanguage(i18n.resolvedLanguage || i18n.language);

  const { helpIndex, findCategoryBySlug, getCategoryPath } = useHelp(language);

  const currentCategory = useMemo(() => {
    if (!helpIndex || !slug) {
      return null;
    }

    return findCategoryBySlug(slug);
  }, [findCategoryBySlug, helpIndex, slug]);

  useTitle(currentCategory?.title, t("common:help"));

  const articlesInCategoryAndSubcategories = useMemo(() => {
    if (!currentCategory || !helpIndex) return [];
    const allCategoryIds = [currentCategory.id, ...currentCategory.children.map((x) => x.id)];
    return helpIndex.articles.filter((article) => allCategoryIds.includes(article.category));
  }, [currentCategory, helpIndex]);

  const getArticlesByCategory = (categoryId: string) => {
    return articlesInCategoryAndSubcategories.filter((x) => x.category == categoryId);
  };

  const categoriesPath = useMemo(() => {
    if (!currentCategory) return [];
    return getCategoryPath(currentCategory);
  }, [currentCategory, getCategoryPath]);

  const breadcrumbItems = useMemo(() => {
    return categoriesPath.map((item, index) => ({
      id: item.id,
      title: item.title,
      to: index < categoriesPath.length - 1 ? item.url : undefined,
    }));
  }, [categoriesPath]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.help.collectionViewed, {
      [analyticProperties.id]: slug,
    });
  });

  return (
    <>
      <Page>
        {categoriesPath && categoriesPath.length > 1 && (
          <Page.Header
            showBack={true}
            navigateTo={categoriesPath[categoriesPath.length - 2].url}
            backSlot={<Breadcrumbs items={breadcrumbItems} separator={"·"} />}
          ></Page.Header>
        )}

        <Page.Content>
          <Stack className={"py-10 px-16 gap-10"}>
            <Title
              size={"lg"}
              header={currentCategory?.title}
              caption={currentCategory?.description}
            />
            {!currentCategory && <Skeleton className={"w-full h-10"} />}
            {currentCategory && articlesInCategoryAndSubcategories.length == 0 && (
              <HelpCollectionCards collections={currentCategory.children} />
            )}
            {currentCategory && articlesInCategoryAndSubcategories.length > 0 && (
              <Stack>
                {getArticlesByCategory(currentCategory.id).length > 0 && (
                  <Box variant={"darkRounded"}>
                    <Stack gap={"sm"}>
                      {getArticlesByCategory(currentCategory.id).map((article) => (
                        <HelpArticleLink article={article} key={`article-${article.id}`} />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Stack gap={"md"}>
                  {currentCategory.children.map((collection) => (
                    <Box variant={"darkRounded"} key={`collection-${collection.id}`}>
                      <Stack gap={"sm"} key={`category-${collection.id}`}>
                        <Link to={`/help/collection/${collection.id}`} className={"pb-(--gap-sm)"}>
                          <Stack direction={"horizontal"} items={"center"}>
                            <Title header={collection.title} />
                            <ButtonIcon
                              variant={"plain"}
                              icon={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
                            />
                          </Stack>
                        </Link>

                        {getArticlesByCategory(collection.id).map((article) => (
                          <HelpArticleLink article={article} key={`article-${article.id}`} />
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
};
